import { ReservedBooking } from "@/gql/graphql";
import {
  StandardTableColumnConfig,
  StandardTableConfigWithNoGroups,
  createColumnConfig,
} from "@stenajs-webui/grid";
import { UNKNOWN_ROUTE_CODE } from "../lib/reservedBookingUtils";
import {
  RebateBookingsColumnKeys,
  rebateBookingsTableConfig,
} from "./RebateBookingsTableConfig";

export type RebateBookingsExcelColumnKeys =
  | RebateBookingsColumnKeys
  | "route"
  | "currency";

// A column config is invariant in its column key type because of onKeyDown, so
// the reused columns need widening to the export's key set. None of them handle
// key presses, and the export never renders, so this is safe.
const reusedColumns = rebateBookingsTableConfig.columns as Record<
  RebateBookingsColumnKeys,
  StandardTableColumnConfig<ReservedBooking, any, RebateBookingsExcelColumnKeys>
>;

/**
 * Reuses the on-screen columns so the export cannot drift from the tables. The
 * export reads itemLabelFormatter, falling back to itemValueResolver, and never
 * renderCell, so the table formats numbers in renderCell to keep them numeric
 * here and summable in Excel. On top of that, every route table ends up in one
 * sheet, so the route and the invoice currency get columns of their own.
 *
 * A column added to the table needs adding to columnOrder here as well, except
 * weightSpacing, which only adds space on screen.
 */
export const rebateBookingsExcelConfig: StandardTableConfigWithNoGroups<
  ReservedBooking,
  RebateBookingsExcelColumnKeys
> = {
  ...rebateBookingsTableConfig,
  columns: {
    ...reusedColumns,
    route: createColumnConfig((item) => item.routeCode ?? UNKNOWN_ROUTE_CODE, {
      columnLabel: "Route",
    }),
    // The table config resolves a missing invoice number to 0 for sorting,
    // which would read as a real invoice number in a spreadsheet.
    invoiceNumber: createColumnConfig((item) => item.invoiceNumber ?? "", {
      columnLabel: "Invoice no.",
    }),
    currency: createColumnConfig((item) => item.invoiceCurrencyCode ?? "", {
      columnLabel: "Currency",
    }),
  },
  columnOrder: [
    "route",
    "bookingNumber",
    "departure",
    "vehicleType",
    "length",
    "weight",
    "vehicleRegNumber",
    "trailerRegNumber",
    "customer",
    "customerReference",
    "invoiceNumber",
    "currency",
    "invoicedAmount",
    "rebateAmountReserved",
  ],
};
